import { useState, useEffect } from 'react';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToBooking = () => {
    const bookingSection = document.getElementById('booking');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Promo Banner */}
      {showBanner && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-teal-600 via-teal-500 to-emerald-500 text-white">
          <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-center relative">
            <div className="flex items-center space-x-3 text-center">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-gift-line text-yellow-300 text-lg"></i>
              </span>
              <p className="text-sm md:text-base font-semibold tracking-wide">
                🎉 First-Time Customers Get <span className="text-yellow-300 font-extrabold">10% OFF</span> All Services $199+ — 
                <button onClick={scrollToBooking} className="underline underline-offset-2 hover:text-yellow-300 transition-colors cursor-pointer whitespace-nowrap font-bold ml-1">Book Now!</button>
              </p>
            </div>
            <button
              onClick={() => setShowBanner(false)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors cursor-pointer"
              aria-label="Close banner"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-close-line text-lg"></i>
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://static.readdy.ai/image/aea82ce1bdb1715498721ef3bf0e643f/dd15f1d56d511aa8aa0a2251c28dcca1.png"
          alt="Garden State Junk Removal Service Truck"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      </div>

      {/* Navigation */}
      <nav className={`fixed left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`} style={{ top: showBanner ? '40px' : '0px' }}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://static.readdy.ai/image/aea82ce1bdb1715498721ef3bf0e643f/3ed1cf1b0ebf95243d711d53c812d3a3.png" 
                alt="Garden State Junk Removal Logo" 
                className="h-12 w-auto"
              />
              <span className={`text-xl font-bold ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                Garden State Junk Removal
              </span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#home" className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>
                Home
              </a>
              <a href="#services" className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>
                Services
              </a>
              <a href="#how-it-works" className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>
                How It Works
              </a>
              <a href="#pricing" className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>
                Pricing
              </a>
              <a href="#booking" className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>
                Free Estimate
              </a>
              <a href="#testimonials" className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>
                Testimonials
              </a>
              <a href="#faq" className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>
                FAQ
              </a>
              <a href="#service-areas" className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>
                Service Areas
              </a>
            </div>
            <button
              onClick={scrollToBooking}
              className="bg-teal-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap"
            >
              Get Free Estimate
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 text-center flex flex-col items-center justify-center">
        <div className="space-y-6">
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
            <i className="ri-recycle-line text-teal-400 text-lg"></i>
            <span className="text-white text-sm font-medium">Serving New Jersey Since 1998</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
            Professional Junk Removal<br />You Can Trust
          </h1>
          
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Fast, reliable, and eco-friendly junk removal, moving, and packing services across all of New Jersey. Over 25 years of experience removing furniture, appliances, and more.
          </p>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
        <i className="ri-arrow-down-line text-white text-3xl"></i>
      </div>
    </section>
  );
}